import React, { useReducer } from "react";

import HistoriaClinicaInicialContext from "./HistoriaClinicaInicialContext";
import HistoriaClinicaInicialReducer from "./HistoriaClinicaInicialReducer";

import { HISTORIA_CLINICA_INICIAL } from "../../types";

import clienteAxios from "../../config/axios";

export default function HistoriaClinicaInicialState(props) {
  const PACIENTE_DUPLICADO_MESSAGE = "Ya existe un paciente registrado con el mismo nombre y apellidos";

  const initialState = {
    historiaClinicaInicial: {},
  };

  const [state, dispatch] = useReducer(HistoriaClinicaInicialReducer, initialState);

  const calculateAgeFromDateString = (dateValue) => {
    if (!dateValue) return 0;

    const normalizedDate = String(dateValue).split("T")[0];
    const [year, month, day] = normalizedDate.split("-").map(Number);

    if (!year || !month || !day) return 0;

    const today = new Date();
    let age = today.getFullYear() - year;
    const currentMonth = today.getMonth() + 1;
    const currentDay = today.getDate();
    const hasHadBirthday =
      currentMonth > month || (currentMonth === month && currentDay >= day);

    if (!hasHadBirthday) {
      age -= 1;
    }

    return age < 0 ? 0 : age;
  };

  const seleccionarHistoriaClinicaInicialPaciente = async (idCatPaciente) => {
    const res = await clienteAxios.post(
      `/expedientemedico/inicial/detalle`,
      { idCatPaciente: idCatPaciente },
      {
        headers: {
          "Access-Control-Allow-Origin": "*",
        },
      }
    );

    if (res.data.paciente.fechaNacimiento) {
      res.data.paciente.edad = calculateAgeFromDateString(res.data.paciente.fechaNacimiento);
      res.data.paciente.fechaNacimiento = res.data.paciente.fechaNacimiento.split("T")[0];
    }

    dispatch({
      type: HISTORIA_CLINICA_INICIAL,
      payload: res.data,
    });

    return res.data;
  };

  const seleccionarHistoriaClinicaOriginalPaciente = async (idCatPaciente) => {
    const res = await clienteAxios.post(
      `/expedientemedico/inicial/detalle`,
      { idCatPaciente: idCatPaciente },
      {
        headers: {
          "Access-Control-Allow-Origin": "*",
        },
      }
    );

    if (res.data.paciente.fechaNacimiento) {
      res.data.paciente.edad = calculateAgeFromDateString(res.data.paciente.fechaNacimiento);
      res.data.paciente.fechaNacimiento = res.data.paciente.fechaNacimiento.split("T")[0];
    }
    return res.data;
  };

  const setHistoriaClinicaInicial = async (historiaClinicaInicial) => {
    dispatch({
      type: HISTORIA_CLINICA_INICIAL,
      payload: historiaClinicaInicial,
    });
  };

  const normalizarHistoriaClinicaInicial = (historiaClinicaInicial) => {
    const paciente = historiaClinicaInicial?.paciente;
    const curp = typeof paciente?.curp === "string" ? paciente.curp.trim().toUpperCase() : paciente?.curp;

    return {
      ...historiaClinicaInicial,
      paciente: paciente
        ? {
            ...paciente,
            curp: curp ? curp : null,
          }
        : paciente,
    };
  };

  const normalizarTextoPaciente = (texto) => {
    if (!texto) return "";

    return String(texto)
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^A-Za-z0-9 ]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase();
  };

  const normalizarNombreCompletoPaciente = (paciente) => {
    return [
      normalizarTextoPaciente(paciente?.nombres),
      normalizarTextoPaciente(paciente?.apellidoPaterno),
      normalizarTextoPaciente(paciente?.apellidoMaterno),
    ].join("|");
  };

  const respuestaError = (message) => ({
    data: {
      message,
      error: "hubo error",
    },
  });

  const validarPacienteDuplicadoPorNombre = async (paciente) => {
    const nombrePacienteActual = normalizarNombreCompletoPaciente(paciente);

    if (!nombrePacienteActual.replace(/\|/g, "")) {
      return null;
    }

    const res = await clienteAxios.get(
      "/pacientes/activos",
      {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Cache-Control": "no-store",
        },
      }
    );

    const pacientes = Array.isArray(res?.data) ? res.data : [];
    return pacientes.find((pacienteExistente) => {
      if (!pacienteExistente) return false;
      if (
        paciente?.idCatPaciente &&
        pacienteExistente.idCatPaciente &&
        Number(paciente.idCatPaciente) === Number(pacienteExistente.idCatPaciente)
      ) {
        return false;
      }

      return normalizarNombreCompletoPaciente(pacienteExistente) === nombrePacienteActual;
    });
  };

  const uploadHistoriaClinicaInicial = async (idCatMedico, resumen, revision, overrides = {}) => {
    try {
      const historiaClinicaInicialNormalizada = normalizarHistoriaClinicaInicial({
        ...state.historiaClinicaInicial,
        ...overrides,
      });

      const pacienteDuplicado = await validarPacienteDuplicadoPorNombre(historiaClinicaInicialNormalizada?.paciente);
      if (pacienteDuplicado) {
        return respuestaError(PACIENTE_DUPLICADO_MESSAGE);
      }

      const res = await clienteAxios.post(
        `/expedientemedico/clinica/inicial/guardar`,
        {
          ...historiaClinicaInicialNormalizada,
          idCatMedico: idCatMedico,
          resumen:
            resumen && resumen != ""
              ? { ...state.historiaClinicaInicial.resumen, resumen: resumen }
              : { ...state.historiaClinicaInicial.resumen },
          revisionCaso:
            revision && revision != ""
              ? { ...state.historiaClinicaInicial.revisionCaso, revisionCaso: revision }
              : { ...state.historiaClinicaInicial.revisionCaso },
        },
        {
          headers: {
            "Access-Control-Allow-Origin": "*",
          },
        }
      );

      return res;
    } catch (error) {
      if (!error.response) {
        error.response = {
          data: {
            message: "Fallo en la conexión al servidor",
            error: "hubo error",
          },
        };
      }

      return error.response;
    }
  };

  return (
    <HistoriaClinicaInicialContext.Provider
      value={{
        historiaClinicaInicial: state.historiaClinicaInicial,
        seleccionarHistoriaClinicaInicialPaciente,
        seleccionarHistoriaClinicaOriginalPaciente,
        uploadHistoriaClinicaInicial,
        setHistoriaClinicaInicial,
      }}>
      {props.children}
    </HistoriaClinicaInicialContext.Provider>
  );
}
